import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usuarioService from '../services/UsuarioService';
import './AdminAlumnoFamiliaresModal.css';

const EMPTY_FORM = {
  nombre: '',
  apellido: '',
  telefono: '',
  email: ''
};

const getItemId = (item) => item?.id || item?.Id || '';
const getItemNombre = (item) => item?.nombre || item?.Nombre || '';
const getItemApellido = (item) => item?.apellido || item?.Apellido || '';
const getItemTelefono = (item) => item?.telefono || item?.Telefono || '';
const getItemEmail = (item) => item?.email || item?.Email || '';

const getAlumnoId = (alumno) => alumno?.id || alumno?.Id || '';

const getAlumnoNombre = (alumno) => {
  if (!alumno) return 'Alumno';
  const nombre = alumno.nombre || alumno.Nombre || '';
  const apellido = alumno.apellido || alumno.Apellido || '';
  const displayName = alumno.displayName || alumno.DisplayName || '';
  const fullName = `${nombre} ${apellido}`.trim();
  return fullName || displayName || alumno.email || alumno.Email || 'Alumno';
};

const getFamiliarNombre = (familiar) => {
  const fullName = `${getItemNombre(familiar)} ${getItemApellido(familiar)}`.trim();
  return fullName || getItemEmail(familiar) || 'Familiar';
};

const AdminAlumnoFamiliaresModal = ({ open, alumno, idColegio, colegioName, onClose, readOnly = false }) => {
  const alumnoId = useMemo(() => getAlumnoId(alumno), [alumno]);
  const alumnoNombre = useMemo(() => getAlumnoNombre(alumno), [alumno]);
  const [familiares, setFamiliares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const loadFamiliares = useCallback(async () => {
    if (!open || !alumnoId || !idColegio) return;

    setLoading(true);
    setError('');
    try {
      const items = await usuarioService.getAlumnoFamiliares(alumnoId, idColegio);
      setFamiliares(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(err.message || 'No pudimos cargar los familiares.');
    } finally {
      setLoading(false);
    }
  }, [open, alumnoId, idColegio]);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setEditingId('');
    setMessage('');
    setMessageType('success');
    setError('');
    setDeleteTarget(null);
    setResetTarget(null);
    loadFamiliares();
  }, [open, loadFamiliares]);

  if (!open) return null;

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setMessage('');
    setMessageType('success');
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId('');
  };

  const handleEdit = (familiar) => {
    setEditingId(getItemId(familiar));
    setForm({
      nombre: getItemNombre(familiar),
      apellido: getItemApellido(familiar),
      telefono: getItemTelefono(familiar),
      email: getItemEmail(familiar)
    });
    setMessage('');
    setMessageType('success');
  };

  const validateForm = () => {
    if (!form.nombre.trim() || !form.apellido.trim() || !form.email.trim()) {
      setMessage('Nombre, apellido y email son obligatorios.');
      setMessageType('error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    setMessage('');
    setMessageType('success');
    try {
      const payload = {
        idColegio,
        nombre: form.nombre.trim(),
        apellido: form.apellido.trim(),
        telefono: form.telefono.trim() || null,
        email: form.email.trim()
      };

      if (editingId) {
        await usuarioService.updateAlumnoFamiliar(alumnoId, editingId, payload);
        setMessage('Familiar actualizado correctamente.');
        setMessageType('success');
      } else {
        await usuarioService.createAlumnoFamiliar(alumnoId, payload);
        setMessage('Familiar creado correctamente. El password inicial es 123456.');
        setMessageType('success');
      }

      resetForm();
      await loadFamiliares();
    } catch (err) {
      setMessage(err.message || 'No pudimos guardar el familiar.');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const familiarId = getItemId(deleteTarget);
    if (!familiarId) return;

    setDeleting(true);
    setMessage('');
    setMessageType('success');
    try {
      await usuarioService.removeAlumnoFamiliar(alumnoId, familiarId, idColegio);
      setDeleteTarget(null);
      setMessage('Familiar quitado correctamente.');
      setMessageType('success');
      await loadFamiliares();
    } catch (err) {
      setMessage(err.message || 'No pudimos quitar el familiar.');
      setMessageType('error');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    const familiarId = getItemId(resetTarget);
    if (!familiarId) return;

    setResetting(true);
    setMessage('');
    setMessageType('success');
    try {
      await usuarioService.resetAlumnoFamiliarPassword(alumnoId, familiarId, idColegio);
      setResetTarget(null);
      setMessage('Contraseña reseteada: el nuevo password es 123456. El familiar deberá cambiarlo luego.');
      setMessageType('success');
    } catch (err) {
      setMessage(err.message || 'No pudimos resetear la contraseña.');
      setMessageType('error');
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-card alumno-familiares-modal-card">
          <div className="alumno-familiares-header">
            <div>
              <p className="eyebrow">Familiares</p>
              <h3>{alumnoNombre}</h3>
              <p>{colegioName ? `Colegio ${colegioName}` : 'Colegio seleccionado'}</p>
            </div>
            <button type="button" className="familiares-close-btn" onClick={onClose} aria-label="Cerrar">
              <i className="fas fa-times" />
            </button>
          </div>

          <div className={`alumno-familiares-layout ${readOnly ? 'read-only' : ''}`}>
            {!readOnly && (
              <form className="familiares-form" onSubmit={handleSubmit}>
                <div className="familiares-form-title">
                  <i className={`fas ${editingId ? 'fa-user-edit' : 'fa-user-plus'}`} />
                  <span>{editingId ? 'Editar familiar' : 'Nuevo familiar'}</span>
                </div>

                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(event) => handleFieldChange('nombre', event.target.value)}
                    disabled={saving}
                  />
                </div>

                <div className="form-field">
                  <label>Apellido</label>
                  <input
                    type="text"
                    value={form.apellido}
                    onChange={(event) => handleFieldChange('apellido', event.target.value)}
                    disabled={saving}
                  />
                </div>

                <div className="form-field">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(event) => handleFieldChange('telefono', event.target.value)}
                    disabled={saving}
                  />
                </div>

                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => handleFieldChange('email', event.target.value)}
                    disabled={saving}
                  />
                </div>

                {message && (
                  <div className={`modal-message familiar-form-message ${messageType}`}>
                    {message}
                  </div>
                )}

                <div className="familiares-form-actions">
                  {editingId && (
                    <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                      Cancelar edición
                    </button>
                  )}
                  <button type="submit" className="btn-primary success" disabled={saving}>
                    {saving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>{editingId ? 'Guardar' : 'Crear familiar'}</span>
                  </button>
                </div>
              </form>
            )}

            <div className="familiares-grid-section">
              <div className="familiares-grid-header">
                <div>
                  <span className="familiares-count">{familiares.length}</span>
                  <span>familiares asociados</span>
                </div>
                <button type="button" className="refresh-btn" onClick={loadFamiliares} disabled={loading}>
                  <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                </button>
              </div>

              {loading && (
                <div className="familiares-empty">
                  <i className="fas fa-spinner fa-spin" />
                  <span>Cargando familiares...</span>
                </div>
              )}

              {!loading && error && (
                <div className="familiares-empty error">
                  <i className="fas fa-exclamation-triangle" />
                  <span>{error}</span>
                </div>
              )}

              {!loading && !error && familiares.length === 0 && (
                <div className="familiares-empty">
                  <i className="fas fa-users-slash" />
                  <span>No hay familiares asociados.</span>
                </div>
              )}

              {!loading && !error && familiares.length > 0 && (
                <div className="familiares-table-wrapper">
                  <table className="data-table familiares-table">
                    <thead>
                      <tr>
                        <th>Familiar</th>
                        <th>Email</th>
                        <th>Teléfono</th>
                        {!readOnly && <th className="text-center width-sm">Acciones</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {familiares.map((familiar) => {
                        const familiarId = getItemId(familiar);
                        return (
                          <tr key={familiarId || getItemEmail(familiar)}>
                            <td>
                              <div className="prof-name">
                                <span className="name">{getFamiliarNombre(familiar)}</span>
                              </div>
                            </td>
                            <td>{getItemEmail(familiar) || '-'}</td>
                            <td>{getItemTelefono(familiar) || '-'}</td>
                            {!readOnly && (
                              <td className="text-center">
                                <div className="action-buttons">
                                  <button
                                    type="button"
                                    className="action-edit-btn"
                                    onClick={() => handleEdit(familiar)}
                                    title="Editar familiar"
                                  >
                                    <i className="fas fa-edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="action-reset-btn"
                                    onClick={() => setResetTarget(familiar)}
                                    title="Resetear contraseña"
                                  >
                                    <i className="fas fa-key" />
                                  </button>
                                  <button
                                    type="button"
                                    className="action-remove-btn"
                                    onClick={() => setDeleteTarget(familiar)}
                                    title="Quitar familiar"
                                  >
                                    <i className="fas fa-trash-alt" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions alumno-familiares-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <div className="modal-overlay familiar-nested-overlay">
          <div className="modal-card familiar-confirm-card">
            <div className="modal-icon">
              <i className="fas fa-trash-alt" />
            </div>
            <h3>Quitar familiar</h3>
            <p>
              Se quitará la asociación de {getFamiliarNombre(deleteTarget)} con el alumno {alumnoNombre}.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancelar
              </button>
              <button type="button" className="btn-primary danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Quitando...' : 'Quitar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="modal-overlay familiar-nested-overlay">
          <div className="modal-card familiar-confirm-card">
            <div className="modal-icon">
              <i className="fas fa-key" />
            </div>
            <h3>Resetear contraseña</h3>
            <p>
              Se va a resetear el password del familiar {getFamiliarNombre(resetTarget)}. El nuevo password será <strong>123456</strong> y el familiar luego deberá cambiarlo.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setResetTarget(null)} disabled={resetting}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleResetPassword} disabled={resetting}>
                {resetting ? 'Reseteando...' : 'Resetear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAlumnoFamiliaresModal;
